import type { NotificationsResponse } from '@/api/generated/model';
import {
  deleteNotification as deleteNotificationAPI,
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '@/api/generated/notifications/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

export interface GetNotificationsParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

export const useNotifications = (params?: GetNotificationsParams) => {
  const queryClient = useQueryClient();

  // Query for fetching notifications
  const {
    data: notificationsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => getNotifications(params || {}),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  // Query for unread count (used in header) - using main query with unreadOnly filter
  const { data: unreadCountResponse } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => getNotifications({ limit: 1, unreadOnly: true }),
    staleTime: 15000, // 15 seconds
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const unreadCount = unreadCountResponse?.unreadCount || 0;

  // Mutation for marking notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationAsRead(id),
    onSuccess: (updatedNotification) => {
      // Update the notifications list in cache
      queryClient.setQueryData<NotificationsResponse>(['notifications', params], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notifications: oldData.notifications.map((notification) =>
            notification.id === updatedNotification.id ? updatedNotification : notification
          ),
          unreadCount: Math.max(0, oldData.unreadCount - 1),
        };
      });

      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
    onError: (error) => {
      toast.error('Failed to mark notification as read');
      console.error('Error marking notification as read:', error);
    },
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllNotificationsAsRead(),
    onSuccess: (response) => {
      // Update all notifications in cache to be read
      queryClient.setQueryData<NotificationsResponse>(['notifications', params], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notifications: oldData.notifications.map((notification) => ({
            ...notification,
            isRead: true,
          })),
          unreadCount: 0,
        };
      });

      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });

      toast.success(`Marked ${response.updatedCount} notifications as read`);
    },
    onError: (error) => {
      toast.error('Failed to mark all notifications as read');
      console.error('Error marking all notifications as read:', error);
    },
  });

  // Mutation for deleting notification
  const deleteNotificationMutation = useMutation({
    mutationFn: (id: string) => deleteNotificationAPI(id),
    onSuccess: (_, deletedId) => {
      // Remove notification from cache
      queryClient.setQueryData<NotificationsResponse>(['notifications', params], (oldData) => {
        if (!oldData) return oldData;
        const deletedNotification = oldData.notifications.find((n) => n.id === deletedId);
        const wasUnread = deletedNotification && !deletedNotification.isRead;

        return {
          ...oldData,
          notifications: oldData.notifications.filter((n) => n.id !== deletedId),
          unreadCount: wasUnread ? Math.max(0, oldData.unreadCount - 1) : oldData.unreadCount,
        };
      });

      // Update unread count if deleted notification was unread
      if (notificationsResponse) {
        const deletedNotification = notificationsResponse.notifications.find(
          (n) => n.id === deletedId
        );
        if (deletedNotification && !deletedNotification.isRead) {
          void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        }
      }

      toast.success('Notification deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete notification');
      console.error('Error deleting notification:', error);
    },
  });

  // Helper functions
  const markAsRead = useCallback(
    (id: string) => {
      markAsReadMutation.mutate(id);
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback(
    (id: string) => {
      deleteNotificationMutation.mutate(id);
    },
    [deleteNotificationMutation]
  );

  const refreshNotifications = useCallback(() => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
  }, [refetch, queryClient]);

  return {
    // Data
    notifications: notificationsResponse?.notifications || [],
    pagination: notificationsResponse?.pagination,
    unreadCount: unreadCount || 0,
    totalUnreadCount: notificationsResponse?.unreadCount || 0,

    // States
    isLoading,
    error,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,

    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
  };
};
